
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import AppStateManager from '../../services/api/appStateManager';

const AppStateContext = createContext();

export function AppStateProvider({ children }) {
  const [appStateManager] = useState(() => AppStateManager.getInstance());
  const [state, setState] = useState(appStateManager.getState());
  const activeSchoolId = useSelector((store) => store.user?.schoolId ?? null);
  const fetchClasses = useCallback(
    (params = {}) => appStateManager.fetchClasses(params),
    [appStateManager]
  );

  useEffect(() => {
    appStateManager.setActiveSchoolId(activeSchoolId);
  }, [appStateManager, activeSchoolId]);

  useEffect(() => {
    const unsubscribe = appStateManager.subscribe((newState) => {
      setState(newState);
    });
    return unsubscribe;
  }, [appStateManager]);

  const value = {
    // ── State ────────────────────────────────────────────────────────────────
    days:        state.days     || [],
    periods:     state.periods  || [],
    classes:     state.schoolId === activeSchoolId ? (state.classes || []) : [],
    loading:     state.loading  || { days: false, periods: false, classes: false },
    errors:      state.errors   || {},
    initialized: state.initialized || false,

    // ── Methods ──────────────────────────────────────────────────────────────
    initializeAfterLogin: ()           => appStateManager.initializeAfterLogin(),
    fetchDays:            ()           => appStateManager.fetchDays(),
    fetchPeriods:         ()           => appStateManager.fetchPeriods(),
    fetchClasses,
    reset:                ()           => appStateManager.reset(),

    // ── Ready flags ──────────────────────────────────────────────────────────
    isDaysReady:    (state.days    || []).length > 0,
    isPeriodsReady: (state.periods || []).length > 0,

    isClassesReady: state.schoolId === activeSchoolId && Array.isArray(state.classes?.data) && state.classes.data.length > 0,
  };

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
}

// ── Base hook ──────────────────────────────────────────────────────────────────

export function useAppState() {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within AppStateProvider');
  }
  return context;
}

// ── Specific hooks ─────────────────────────────────────────────────────────────

export function useDays() {
  const { days, loading, errors } = useAppState();
  return {
    days,
    loading: loading?.days  || false,
    error:   errors?.days   || null,
  };
}

export function usePeriods() {
  const { periods, loading, errors } = useAppState();
  return {
    periods,
    loading: loading?.periods || false,
    error:   errors?.periods  || null,
  };
}

export function useClasses() {
  const { classes, loading, errors, fetchClasses, isClassesReady } = useAppState();
  return {
    classes,                              // full { success, data } response
    loading: loading?.classes || false,
    error:   errors?.classes  || null,
    fetchClasses,                         // (params = {}) => Promise
    isClassesReady,                       // true when classes.data has items
  };
}